'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './tooltip.module.scss';
import { IconTooltip } from '@/assets/icons/IconTooltip';
import cn from 'classnames';

const TT_WIDTH = 160;

type TooltipPositionType = 'top' | 'left' | 'right' | 'bottom';

export interface TooltipProps {
    children: React.ReactNode;
    position?: TooltipPositionType;
    ariaLabel?: string;
    className?: string;
    contentClassName?: string;
    buttonText?: string;
    updateKey?: number;
    defaultOpen?: boolean;
}

function useTooltipPosition(
    ref: React.RefObject<HTMLDivElement | null>,
    initialPosition: TooltipPositionType,
    updateKey?: number,
) {
    const [position, setPosition] = useState(initialPosition);

    const updatePosition = useCallback(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const viewportWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth;

            if (initialPosition === 'right' && rect.right + TT_WIDTH > viewportWidth) {
                setPosition('left');
            } else if (initialPosition === 'left' && rect.left - TT_WIDTH < 0) {
                setPosition('right');
            } else {
                setPosition(initialPosition);
            }
        }
    }, [ref, initialPosition]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;

        const updatePositionWithDelay = () => {
            timeoutId = setTimeout(updatePosition, 300);
        };

        updatePositionWithDelay();
        return () => {
            clearTimeout(timeoutId);
        };
    }, [updatePosition, updateKey]);

    useEffect(() => {
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('resize', updatePosition);
        };
    }, [updatePosition]);

    return position;
}

export function Tooltip({
    children,
    position = 'right',
    ariaLabel,
    className,
    buttonText,
    contentClassName,
    updateKey,
    defaultOpen = false,
}: TooltipProps): React.JSX.Element {
    const [isVisible, setIsVisible] = useState<boolean>(defaultOpen ?? false);
    const ttWrapperRef = useRef<HTMLDivElement | null>(null);
    const newPosition = useTooltipPosition(ttWrapperRef, position, updateKey);

    const handleMouseEnter = useCallback((): void => {
        setIsVisible(true);
    }, []);

    const handleMouseLeave = useCallback((): void => {
        setIsVisible(false);
    }, []);

    const handleClick = useCallback((event: React.SyntheticEvent): void => {
        const mouseEvent = event.nativeEvent as MouseEvent;

        // Check if the event is triggered by a mouse (detail > 0) or by a keyboard (detail === 0)
        if (mouseEvent.detail > 0) {
            setIsVisible(true);
        }
    }, []);

    const handleKeyUp = useCallback((event: React.KeyboardEvent): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsVisible((prevIsVisible) => !prevIsVisible);
        } else if (event.key === 'Escape') {
            setIsVisible(false);
        }
    }, []);

    const handleBlur = useCallback((event: React.FocusEvent<HTMLButtonElement>): void => {
        if (ttWrapperRef.current && !ttWrapperRef.current.contains(event.relatedTarget as Node)) {
            setIsVisible(false);
        }
    }, []);

    const wrapperCn = cn(styles.ttWrapper, className, {
        [styles.active]: isVisible,
    });

    const contentCn = cn(styles.ttContent, contentClassName, {
        [styles[newPosition]]: newPosition,
    });

    return (
        <span className={wrapperCn} ref={ttWrapperRef}>
            <button
                className={styles.ttButton}
                aria-expanded={isVisible}
                aria-label={ariaLabel}
                data-cy="atoms-tooltip-button"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onBlur={handleBlur}
                onKeyUp={handleKeyUp}
                onClick={handleClick}
            >
                {buttonText ? buttonText : <IconTooltip />}
            </button>
            {isVisible && (
                <span className={contentCn} data-cy="atoms-tooltip-content">
                    {children}
                </span>
            )}
        </span>
    );
}
